import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useRouteError, isRouteErrorResponse, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Sentry } from '@/lib/sentry';
import { supabase } from '@/api/supabase';
import { AuthContext } from '@/hooks/useAuth';
import { FavoritesProvider } from '@/contexts/FavoritesContext';
import { CompareProvider } from '@/contexts/CompareContext';
import { ToastProvider } from '@/components/ui/Toast';
import { ConfirmProvider } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/components/ui/Toast';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BottomNav } from '@/components/BottomNav';
import { ScrollToTop } from '@/components/ScrollToTop';
import { AndroidBackHandler } from '@/components/AndroidBackHandler';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { useShowBottomNav } from '@/lib/platform';
import { RouteSkeleton } from '@/components/RouteSkeleton';
import type { Session, User } from '@supabase/supabase-js';

const Home = lazy(() => import('@/pages/Home').then(m => ({ default: m.Home })));
const PropertyDetail = lazy(() => import('@/pages/PropertyDetail').then(m => ({ default: m.PropertyDetail })));
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })));
const AuthCallback = lazy(() => import('@/pages/AuthCallback').then(m => ({ default: m.AuthCallback })));
const NewListing = lazy(() => import('@/pages/NewListing').then(m => ({ default: m.NewListing })));
const EditListing = lazy(() => import('@/pages/EditListing').then(m => ({ default: m.EditListing })));
const MyListings = lazy(() => import('@/pages/MyListings').then(m => ({ default: m.MyListings })));
const Feature = lazy(() => import('@/pages/Feature').then(m => ({ default: m.Feature })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Profile = lazy(() => import('@/pages/Profile').then(m => ({ default: m.Profile })));
const Premium = lazy(() => import('@/pages/Premium').then(m => ({ default: m.Premium })));
const ComoFunciona = lazy(() => import('@/pages/ComoFunciona').then(m => ({ default: m.ComoFunciona })));
const Termos = lazy(() => import('@/pages/Termos').then(m => ({ default: m.Termos })));
const Privacidade = lazy(() => import('@/pages/Privacidade').then(m => ({ default: m.Privacidade })));
const Favorites = lazy(() => import('@/pages/Favorites').then(m => ({ default: m.Favorites })));
const Compare = lazy(() => import('@/pages/Compare').then(m => ({ default: m.Compare })));
const MapPage = lazy(() => import('@/pages/MapPage').then(m => ({ default: m.MapPage })));
const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AdminPanel = lazy(() => import('@/pages/AdminPanel').then(m => ({ default: m.AdminPanel })));

function PageLoader() {
  const { pathname } = useLocation();
  return (
    <div aria-busy="true" aria-label="Carregando..." className="min-h-[50vh]">
      <RouteSkeleton pathname={pathname} />
    </div>
  );
}

function RootErrorBoundary() {
  const error = useRouteError();
  const message = isRouteErrorResponse(error)
    ? `${error.status} - ${error.statusText}`
    : 'Algo deu errado.';

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-4 text-center">
      <p className="font-display text-2xl font-semibold text-ink">{message}</p>
      <Link to="/" className="text-brand font-medium hover:underline">
        Voltar ao início
      </Link>
    </div>
  );
}

function Layout() {
  const { pathname } = useLocation();
  const isMap = pathname === '/mapa';
  const showBottomNav = useShowBottomNav();

  return (
    <div className={[
      'app-aurora flex flex-col bg-faint',
      isMap ? 'h-screen overflow-hidden' : 'min-h-screen',
    ].join(' ')}>
      <ScrollToTop />
      <AndroidBackHandler />
      <Header />
      <div className={[
        'flex-1',
        isMap ? 'overflow-hidden min-h-0' : 'min-h-[calc(100vh-16rem)]',
        showBottomNav ? 'pb-[calc(4rem+env(safe-area-inset-bottom))]' : '',
      ].join(' ')}>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </div>
      {!isMap && !showBottomNav && (
        <div>
          <Footer />
        </div>
      )}
      {showBottomNav && <BottomNav />}
    </div>
  );
}

function AuthLayout() {
  const showBottomNav = useShowBottomNav();

  // No mobile (app nativo ou viewport pequeno): sem Header global — o form já traz o
  // logo —, mas mantém o menu inferior (bottom nav). No desktop web: visual limpo só logo.
  if (showBottomNav) {
    return (
      <div className="app-aurora flex min-h-screen flex-col bg-faint">
        <ScrollToTop />
        <AndroidBackHandler />
        <div className="flex flex-1 flex-col pb-[calc(4rem+env(safe-area-inset-bottom))]">
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="app-aurora flex min-h-screen flex-col bg-faint">
      <ScrollToTop />
      <AndroidBackHandler />
      <div className="flex flex-1 flex-col">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AuthLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      { path: 'login', element: <Login /> },
      { path: 'auth/callback', element: <AuthCallback /> },
      { path: 'reset-password', element: <ResetPassword /> },
    ],
  },
  {
    path: '/',
    element: <Layout />,
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <Home /> },
      { path: 'imovel/:id', element: <PropertyDetail /> },
      {
        path: 'perfil',
        element: <ProtectedRoute><Profile /></ProtectedRoute>,
      },
      {
        path: 'anunciar',
        element: <ProtectedRoute><NewListing /></ProtectedRoute>,
      },
      {
        path: 'editar/:id',
        element: <ProtectedRoute><EditListing /></ProtectedRoute>,
      },
      {
        path: 'meus-anuncios',
        element: <ProtectedRoute><MyListings /></ProtectedRoute>,
      },
      {
        path: 'destacar/:id',
        element: <ProtectedRoute><Feature /></ProtectedRoute>,
      },
      {
        path: 'premium',
        element: <Premium />,
      },
      {
        path: 'como-funciona',
        element: <ComoFunciona />,
      },
      {
        path: 'termos',
        element: <Termos />,
      },
      {
        path: 'privacidade',
        element: <Privacidade />,
      },
      {
        path: 'favoritos',
        element: <ProtectedRoute><Favorites /></ProtectedRoute>,
      },
      {
        path: 'comparar',
        element: <Compare />,
      },
      {
        path: 'mapa',
        element: <MapPage />,
      },
      {
        path: 'painel',
        element: <ProtectedRoute><Dashboard /></ProtectedRoute>,
      },
      {
        path: 'admin',
        element: <AdminRoute><AdminPanel /></AdminRoute>,
      },
    ],
  },
]);

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<import('@/types/database').Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const hadUserRef = useRef(false);
  const lastProfileUidRef = useRef<string | null>(null);

  async function fetchProfile(uid: string) {
    const { data } = await supabase.from('profiles').select('*').eq('id', uid).single();
    if (data) setProfile(data as import('@/types/database').Profile);
    lastProfileUidRef.current = uid;
  }

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const sess = data.session;
      setSession(sess);
      setUser((prev) => (prev?.id === (sess?.user?.id ?? null) ? prev : (sess?.user ?? null)));
      if (sess?.user) void fetchProfile(sess.user.id);
      hadUserRef.current = !!sess?.user;
      setLoading(false);
      initializedRef.current = true;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      const hadUser = hadUserRef.current;
      const hasUserNow = !!sess?.user;

      setSession(sess);
      setUser((prev) => (prev?.id === (sess?.user?.id ?? null) ? prev : (sess?.user ?? null)));
      Sentry.setUser(sess?.user ? { id: sess.user.id } : null);
      if (!sess?.user) {
        setProfile(null);
        lastProfileUidRef.current = null;
      } else if (
        event === 'USER_UPDATED' ||
        (event === 'SIGNED_IN' && (!hadUser || lastProfileUidRef.current !== sess.user.id)) ||
        (event === 'INITIAL_SESSION' && (!hadUser || lastProfileUidRef.current !== sess.user.id))
      ) {
        void fetchProfile(sess.user.id);
      }

      if (initializedRef.current && event === 'SIGNED_IN' && !hadUser && hasUserNow) {
        showToast('Login realizado com sucesso.', 'success');
      }
      if (initializedRef.current && event === 'SIGNED_OUT' && hadUser && !hasUserNow) {
        showToast('Logout realizado com sucesso.', 'success');
      }
      hadUserRef.current = hasUserNow;
    });

    return () => subscription.unsubscribe();
  }, [showToast]);

  async function signOut() {
    // Clear local state eagerly so the UI reflects sign-out even if the
    // network call fails or the auth listener never fires.
    setSession(null);
    setUser(null);
    setProfile(null);
    lastProfileUidRef.current = null;
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <AuthProvider>
          <FavoritesProvider>
            <CompareProvider>
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </CompareProvider>
          </FavoritesProvider>
        </AuthProvider>
      </ToastProvider>
    </HelmetProvider>
  );
}
